import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SearchResult from './LARGENAVBAR/SearchResult';
import { useDebounce } from './../../hooks/useDebounce';
import { useLocation } from 'react-router-dom';
import { getAllProducts, getProductSearch } from '../../apiCalls/urlFunction';

const sanitizeEdges = (edges) => {
  if (!Array.isArray(edges)) return [];

  return edges?.map(({ node }) => {
    if (!node || typeof node !== "object") return node;

    const result = {};

    for (const key in node) {
      const value = node[key];

      if (value && Array.isArray(value.edges)) {
        result[key] = sanitizeEdges(value.edges);
      } else {
        result[key] = value;
      }
    }

    return result;
  });
};

const TYPEWRITER_PLACEHOLDER = "Search Your Favourite Brand Gift Card";

function NavbarSearch({ autoFocus = false }) {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchResultData, setSearchResultData] = useState([]);
  const [showProductList, setShowProductList] = useState(false);
  const [resultFound, setResultFound] = useState(false);
  const [indexedProducts, setIndexedProducts] = useState([]);


  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Track if we're waiting for backend response
  const [isBackendFetching, setIsBackendFetching] = useState(false);
  const latestSearchKeywordRef = useRef('');

  // Typewriter placeholder state
  const [placeholderText, setPlaceholderText] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const searchDebounce = useDebounce(inputValue, 300);

  const resultBoxRef = useRef(null);
  const inputRef = useRef(null);

  const location = useLocation();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Typewriter effect for placeholder
  useEffect(() => {
    if (isInputFocused || inputValue) {
      return;
    }

    let index = 0;
    let timeoutId = null;
    let isPaused = false;

    const typeNextChar = () => {
      if (isPaused) return;

      if (index <= TYPEWRITER_PLACEHOLDER.length) {
        setPlaceholderText(TYPEWRITER_PLACEHOLDER.slice(0, index));
        index += 1;
        timeoutId = setTimeout(typeNextChar, 80);
      } else {
        isPaused = true;
        timeoutId = setTimeout(() => {
          index = 0;
          isPaused = false;
          setPlaceholderText('');
          timeoutId = setTimeout(typeNextChar, 300);
        }, 2000);
      }
    };

    setPlaceholderText('');
    timeoutId = setTimeout(typeNextChar, 300);

    return () => {
      isPaused = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInputFocused, inputValue]);

  // Helper to normalize strings for robust comparison (handles punctuation, extra spaces)
  const normalize = (str) => {
    return (str || '')
      .toString()
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Function to score product based on NAME matching only (ultra fast)
  const scoreProduct = (product, keyword) => {
    if (!product || !keyword) return 0;

    const rawKeyword = (keyword || '').trim().toLowerCase();
    if (!rawKeyword) return 0;

    const normKeyword = normalize(rawKeyword);
    const keywordTokens = normKeyword.split(' ').filter(Boolean);

    const rawName = product._rawName !== undefined ? product._rawName : (product?.name || '').toLowerCase();
    const normName = product._normName !== undefined ? product._normName : normalize(product?.name);
    const normSlug = product._normSlug !== undefined ? product._normSlug : normalize(product?.slug);

    // If neither name nor slug contains the keyword or any of its tokens, strictly exclude it
    const hasSubstrMatch =
      rawName.includes(rawKeyword) ||
      normName.includes(normKeyword) ||
      normSlug.includes(normKeyword);

    const anyTokenMatch =
      keywordTokens.length > 0 &&
      keywordTokens.some((token) => normName.includes(token) || normSlug.includes(token));

    if (!hasSubstrMatch && !anyTokenMatch) {
      return 0;
    }

    let score = 0;

    // 1. Direct exact match on name
    if (rawName === rawKeyword || normName === normKeyword) {
      score += 1000;
    }
    // 2. Name starts with keyword
    else if (rawName.startsWith(rawKeyword) || normName.startsWith(normKeyword)) {
      score += 500;
    }
    // 3. Name contains full keyword phrase
    else if (rawName.includes(rawKeyword) || normName.includes(normKeyword)) {
      score += 300;
    }
    // 4. Slug match
    else if (normSlug.startsWith(normKeyword) || normSlug.includes(normKeyword)) {
      score += 200;
    }

    // 5. Multi-token matching in product name (e.g. "amazon card", "apple ae")
    if (keywordTokens.length > 0) {
      let tokensMatchedInName = 0;

      for (const token of keywordTokens) {
        if (normName.includes(token) || normSlug.includes(token)) {
          tokensMatchedInName++;
        }
      }

      if (tokensMatchedInName === keywordTokens.length) {
        score += 400;
      } else if (tokensMatchedInName > 0) {
        score += tokensMatchedInName * 60;
      }
    }

    return score;
  };

  // Local search function using indexed products (INSTANT response <2ms)
  const searchProductInPrefetchProducts = (keyword) => {
    const cleanKeyword = (keyword || '').trim();

    if (!cleanKeyword) {
      setSearchResultData([]);
      setShowProductList(false);
      setResultFound(false);
      return [];
    }

    const scored = indexedProducts
      .map((item) => ({
        product: item,
        score: scoreProduct(item, cleanKeyword),
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30) // Cap to top 30 to prevent laggy DOM rendering
      .map((item) => item.product);

    setSearchResultData(scored);
    setShowProductList(true);
    setResultFound(scored.length === 0);
    return scored;
  };

  // Backend search function (fetches fresh live data with latest discounts)
  const fetchFromBackend = async (keyword) => {
    const cleanKeyword = (keyword || '').trim().toLowerCase();
    if (!cleanKeyword) return;

    setIsBackendFetching(true);

    try {
      const response = await getProductSearch({ q: cleanKeyword });

      if (response?.status) {
        const sanitized = sanitizeEdges(response?.data?.edges);

        if (sanitized && sanitized.length > 0) {
          const indexedSanitized = sanitized.map((item) => ({
            ...item,
            _rawName: (item?.name || '').toLowerCase(),
            _normName: normalize(item?.name),
            _normSlug: normalize(item?.slug),
          }));

          // Update catalog cache with fresh data so newest discounts are stored
          setIndexedProducts((prev) => {
            const freshMap = new Map(indexedSanitized.map((p) => [p.id, p]));
            const updated = prev.map((old) => freshMap.get(old.id) || old);
            const existingIds = new Set(prev.map((p) => p.id));
            for (const item of indexedSanitized) {
              if (!existingIds.has(item.id)) {
                updated.push(item);
              }
            }
            return updated;
          });

          // Ensure user is still on this query
          if (latestSearchKeywordRef.current.trim().toLowerCase() === cleanKeyword) {
            const scored = indexedSanitized
              .map((item) => ({
                product: item,
                score: scoreProduct(item, cleanKeyword),
              }))
              .filter((item) => item.score > 0)
              .sort((a, b) => b.score - a.score)
              .slice(0, 30)
              .map((item) => item.product);

            if (scored.length > 0) {
              setSearchResultData((current) => {
                const freshMap = new Map(scored.map((p) => [p.id, p]));
                if (current && current.length > 0) {
                  return current.map((item) => freshMap.get(item.id) || item);
                }
                return scored;
              });
              setShowProductList(true);
              setResultFound(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Backend search error:', error);
    } finally {
      setIsBackendFetching(false);
    }
  };

  const getStoreAllProducts = async () => {
    try {
      const payload = {
        limit: 10000,
      };
      const res = await getAllProducts(payload);
      const sanitizedData = sanitizeEdges(res?.data?.edges);

      if (sanitizedData?.length > 0) {
        const indexed = sanitizedData.map((item) => ({
          ...item,
          _rawName: (item?.name || '').toLowerCase(),
          _normName: normalize(item?.name),
          _normSlug: normalize(item?.slug),
        }));
        setIndexedProducts(indexed);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch products on mount
  useEffect(() => {
    getStoreAllProducts();
  }, []);

  // Background fetch for fresh live prices on debounced term
  useEffect(() => {
    const trimmed = searchDebounce?.trim();
    latestSearchKeywordRef.current = trimmed || '';

    if (trimmed && trimmed.length >= 1) {
      fetchFromBackend(trimmed);
    }
  }, [searchDebounce]);

  // Hide dropdown on route change
  useEffect(() => {
    setInputValue('');
    setShowProductList(false);
  }, [location.pathname]);

  // Hide dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsideResult =
        resultBoxRef.current &&
        resultBoxRef.current.contains(event.target);

      const clickedInsideInput =
        inputRef.current &&
        inputRef.current.contains(event.target);

      if (!clickedInsideResult && !clickedInsideInput) {
        setShowProductList(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const keyword = e.target.value;

    if (keyword.length <= 100) {
      setInputValue(keyword);
      setSelectedIndex(-1);

      const trimmed = keyword.trim();
      latestSearchKeywordRef.current = trimmed;

      if (trimmed.length >= 1) {
        // INSTANT synchronous local search (0ms latency)
        searchProductInPrefetchProducts(trimmed);
      } else {
        setShowProductList(false);
        setSearchResultData([]);
        setResultFound(false);
      }
    }
  };

  // Clear input
  const handleClearInput = () => {
    setInputValue('');
    setSearchResultData([]);
    setShowProductList(false);
    setResultFound(false);
    latestSearchKeywordRef.current = '';
  };

  const handleKeyDown = (e) => {
    if (!showProductList || searchResultData.length === 0) {
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setSelectedIndex((prev) =>
        prev < searchResultData.length - 1 ? prev + 1 : 0
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : searchResultData.length - 1
      );
    }

    if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();

      const selectedProduct = searchResultData[selectedIndex];

      if (selectedProduct?.slug) {
        window.location.href = `/card-details/${selectedProduct.slug}`;
      }
    }

    if (e.key === "Escape") {
      e.preventDefault();

      setShowProductList(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        position: 'relative',
        top: {xs:15 , sm :10, md:0.5},
        mb: { xs: 2, sm: 0, md: 0, lg: 0 },
      }}
    >
      {/* Search Wrapper */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#FFFFFF',
          border: isInputFocused ? "1.5px solid #CF9D2E" : "1.5px solid #E4C18F",
          borderRadius: '12px',
          px: 1.5,
          py:{ xs :0.4 ,md:0.8} ,
          position: 'relative',
          boxShadow: isInputFocused
            ? "0 4px 16px rgba(207, 157, 46, 0.2)"
            : "0 2px 8px rgba(0, 0, 0, 0.04)",
          transition: "all 0.2s ease-in-out",
        }}
      >
        {/* Left Search Icon */}
        <SearchIcon
          sx={{
            color: isInputFocused ? '#CF9D2E' : '#8D5A24',
            fontSize: { xs: 22, sm: 24 },
            mr: 1,
            flexShrink: 0,
            transition: 'color 0.2s ease',
          }}
        />

        {/* Input */}
        <Box
          component="input"
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsInputFocused(true);
            if (indexedProducts.length === 0) {
              getStoreAllProducts();
            }
            if (inputValue.trim().length >= 1) {
              searchProductInPrefetchProducts(inputValue.trim());
              setShowProductList(true);
            } else if (searchResultData?.length > 0) {
              setShowProductList(true);
            }
          }}
          onBlur={() => {
            setIsInputFocused(false);
          }}
          placeholder={
            isInputFocused || inputValue ? '' : placeholderText
          }
          sx={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            fontSize: { xs: "13px", sm: "14px", md: "15px" },
            outline: 'none',
            fontFamily: 'Be Vietnam Pro',
            fontWeight: 500,
            color: '#111827',
            py: 0.8,
            '&::placeholder': {
              color: '#6B7280',
              opacity: 0.9,
              fontFamily: 'Be Vietnam Pro',
              fontSize: {
                xs: '12px',
                sm: '13px',
                md: '14px',
              },
            },
          }}
        />

        {/* Right Side Loading / Clear Button */}
        {isBackendFetching && inputValue && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#4CAF50',
              mr: 1,
              flexShrink: 0,
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.3, transform: 'scale(0.8)' },
                '50%': { opacity: 1, transform: 'scale(1.2)' },
                '100%': { opacity: 0.3, transform: 'scale(0.8)' },
              },
            }}
          />
        )}

        {inputValue && (
          <IconButton
            size="small"
            onClick={handleClearInput}
            sx={{
              p: 0.5,
              color: '#6B7280',
              bgcolor: 'rgba(0,0,0,0.05)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.1)',
                color: '#111827',
              },
              transition: 'all 0.15s ease',
            }}
            aria-label="Clear search"
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* Search Result Dropdown */}
      <SearchResult
        loading={searchResultData.length === 0 && (loading || isBackendFetching)}
        showProductList={showProductList}
        resultBoxRef={resultBoxRef}
        searchResultData={searchResultData}
        resultNotFound={resultFound && !isBackendFetching && searchResultData.length === 0}
        searchTerm={inputValue}
        selectedIndex={selectedIndex}
      />
    </Box>
  );
}

export default NavbarSearch;
